import { Text } from '@linktr.ee/arbor/components/Text';
import { CircleNotchIcon, CaretLeftIcon } from '@linktr.ee/arbor/icons';
import { useFlags } from '@linktr.ee/feature-flags-mfe';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@shared/components/Button';
import { InputText } from '@shared/components/InputText';
import Layout from '@shared/components/Layout';
import MarketingConsent from '@shared/components/MarketingConsent';
import UsernameSuggestion from '@shared/components/UsernameSuggestion';
import {
  publishSignupStartedEvent,
  publishSignupErrorEncounteredEvent,
} from '@shared/utils/analytics/events';
import { usePublishScreenViewedEvent } from '@shared/utils/analytics/hooks';
import { parseDescopeError } from '@shared/utils/descopeErrorParser';
import { validateUsername } from '@shared/utils/validation';
import { translateValidationError } from '@shared/utils/validationMessages';
import bannerSignupImage from '@universal-login/images/banner-signup-desktop.webp';
import {
  UsernameScreenProps,
  ParsedDescopeUsernameError,
} from '@universal-login/types/descope';

const UsernameScreen: React.FC<UsernameScreenProps> = ({
  onClick,
  onChange,
  errorText,
  isSocialUsername = false,
  resetError,
  onBackButtonClick,
  onValidateUsername,
  context,
  prefilledUsername,
}) => {
  const { t } = useTranslation();
  const contextEmail = context.form?.email;
  const email = typeof contextEmail === 'string' ? contextEmail : undefined;

  usePublishScreenViewedEvent('Sign Up - Choose Username');

  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasUsernameUpdated, setHasUsernameUpdated] = useState(false);
  const [showMarketingCheckbox, setShowMarketingCheckbox] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState<boolean | null>(null);
  const [usernameRegexError, setUsernameRegexError] = useState<
    string | undefined
  >();
  const [isValidating, setIsValidating] = useState(false);
  const { getFlag } = useFlags();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const onChangeRef = useRef(onChange);
  const onValidateUsernameRef = useRef(onValidateUsername);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onValidateUsernameRef.current = onValidateUsername;
  }, [onValidateUsername]);

  // Parse the Descope error to extract message and suggestions
  const parsedError: ParsedDescopeUsernameError | null = useMemo(() => {
    if (!errorText) {
      return null;
    }
    return parseDescopeError(errorText);
  }, [errorText]);

  useEffect(() => {
    const sanitizedPrefilled = prefilledUsername?.replace(/\s/g, '');
    if (sanitizedPrefilled) {
      setUsername(sanitizedPrefilled);
      const formFieldName = isSocialUsername
        ? 'form.socialSignupUsername'
        : 'form.emailPasswordlessSignupUsername';
      onChangeRef.current({
        [formFieldName]: sanitizedPrefilled,
      });
    } else if (email) {
      const [emailPrefix = ''] = email.split('@');
      const emailUsername = emailPrefix.replace(/[^a-zA-Z0-9_.]/g, '');
      setUsername(emailUsername);
      const formFieldName = isSocialUsername
        ? 'form.socialSignupUsername'
        : 'form.emailPasswordlessSignupUsername';
      onChangeRef.current({
        [formFieldName]: emailUsername,
      });
    }
  }, [email, isSocialUsername, prefilledUsername]);

  // Reset hasUsernameUpdated when a new error comes in
  useEffect(() => {
    if (errorText) {
      setHasUsernameUpdated(false);
    }
  }, [errorText]);

  // Feature flag logic for marketing consent
  useEffect(() => {
    if (isSocialUsername) {
      onChangeRef.current({ 'form.signupMarketingOptIn': 'null' });
      const showMarketingCheckbox = Boolean(
        getFlag('gdpr-marketing-consent-checkbox-signup-mfe')
      );
      setShowMarketingCheckbox(showMarketingCheckbox);

      if (showMarketingCheckbox) {
        setMarketingOptIn(false); // Sets the marketing checkbox to false/unchecked by default if the user is in a gdpr country
        onChangeRef.current({ 'form.signupMarketingOptIn': 'false' });
      }
    }
  }, [getFlag, isSocialUsername]);

  // Async validation with debounce
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't validate empty username
    if (!username) {
      setIsValidating(false);
      return;
    }

    // Set validating state immediately when user types
    setIsValidating(true);

    // Debounce the validation
    debounceTimerRef.current = setTimeout(async () => {
      try {
        // Perform async validation
        const validation = await Promise.resolve(validateUsername(username));

        if (!validation.isValid) {
          setUsernameRegexError(validation.error);
        } else {
          setUsernameRegexError(undefined);
          await onValidateUsernameRef.current?.();
        }
      } finally {
        setIsValidating(false);
      }
    }, 500); // 500ms debounce delay

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [username]);

  const onMarketingOptInChange = (checked: boolean) => {
    setMarketingOptIn(checked);
    onChange({ 'form.signupMarketingOptIn': checked ? 'true' : 'false' });
  };

  const handleUsernameChange = (value: string) => {
    const sanitizedValue = value.replace(/\s/g, '');

    // If the sanitized value is the same as the current username (e.g. user
    // pressed space), skip clearing errors — otherwise errors get dismissed
    // without re-validation because setUsername is a no-op for the same value
    // and the useEffect that triggers debounced validation won't re-fire.
    if (sanitizedValue === username) {
      return;
    }

    // Clear regex error when user starts typing
    resetError();
    setUsernameRegexError(undefined);
    setHasUsernameUpdated(true);

    // Update username value
    setUsername(sanitizedValue);
    const formFieldName = isSocialUsername
      ? 'form.socialSignupUsername'
      : 'form.emailPasswordlessSignupUsername';
    onChange?.({
      [formFieldName]: sanitizedValue,
    });
  };

  const handleSuggestionClick = (suggestion: string) => {
    const sanitizedSuggestion = suggestion.replace(/\s/g, '');

    resetError();
    setUsernameRegexError(undefined); // Clear validation error
    setUsername(sanitizedSuggestion);
    setHasUsernameUpdated(true); // Reset the flag when a suggestion is clicked
    const formFieldName = isSocialUsername
      ? 'form.socialSignupUsername'
      : 'form.emailPasswordlessSignupUsername';
    const formData = { [formFieldName]: sanitizedSuggestion };
    onChange(formData);
  };

  const submitUsername = async () => {
    // Validate username before submitting
    if (username) {
      const validation = validateUsername(username);
      if (!validation.isValid) {
        setUsernameRegexError(validation.error);
        return;
      }
    }

    setIsLoading(true);
    try {
      const formFieldName = isSocialUsername
        ? 'form.socialSignupUsername'
        : 'form.emailPasswordlessSignupUsername';
      if (!isSocialUsername) {
        publishSignupStartedEvent('Email OTP');
      }
      const formData = { [formFieldName]: username };
      onChange(formData);
      await onClick?.(); // Continue button interaction
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Username submission failed';
      const signupMethod = isSocialUsername ? 'Social' : 'Email OTP';
      publishSignupErrorEncounteredEvent(signupMethod, errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = username.length > 0 && !usernameRegexError;

  const handleContinue = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    await submitUsername();
  };

  const handleInputKeyDown = async (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter' && isFormValid && !isLoading) {
      e.preventDefault();
      await submitUsername();
    }
  };

  return (
    <Layout
      heading={
        isSocialUsername ? (
          t('Welcome to Linktree!')
        ) : (
          <>
            <div className="relative w-20 mb-3 sm:left-28">
              <Button
                onClick={async () => {
                  await onBackButtonClick?.();
                }}
                variant="ghost"
                fullWidth={false}
                icon={<CaretLeftIcon />}
                label={t('Back')}
              />
            </div>
            <span className="text-center">{t('Choose your username')}</span>
          </>
        )
      }
      subHeading={
        isSocialUsername
          ? t('Choose your Linktree username. You can always change it later.')
          : undefined
      }
      variant="banner"
      bannerImageDesktop={bannerSignupImage}
    >
      <div className="space-y-4">
        {!isSocialUsername && (
          <Text
            as="p"
            variant="body-small-regular"
            className="text-gray-600 text-center"
          >
            {t(
              'Try something similar to your social handles for easy recognition.'
            )}
          </Text>
        )}

        <div className="relative">
          <InputText
            label={t('username')}
            value={username}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleUsernameChange(e.target.value);
            }}
            onKeyDown={handleInputKeyDown}
            prefix={'linktr.ee/'}
            state={
              usernameRegexError || errorText || parsedError
                ? 'error'
                : 'default'
            }
            errorMessage={translateValidationError(t, usernameRegexError)}
            icon={
              isValidating && username ? (
                <CircleNotchIcon className="size-5 animate-spin" />
              ) : undefined
            }
            autoFocus
          />
        </div>

        {parsedError && !hasUsernameUpdated && (
          <>
            <Text
              as="span"
              variant="body-small-regular"
              className="text-red-600 mb-4 ml-4"
            >
              {parsedError.message}
            </Text>
            <UsernameSuggestion
              suggestions={parsedError.suggestions}
              onSuggestionClick={handleSuggestionClick}
            />
          </>
        )}

        {errorText && !parsedError && !hasUsernameUpdated && (
          <Text
            as="p"
            variant="body-small-regular"
            className="text-red-600 text-center"
          >
            {errorText}
          </Text>
        )}

        {isSocialUsername && (
          <MarketingConsent
            show={showMarketingCheckbox}
            checked={marketingOptIn || false}
            onChange={onMarketingOptInChange}
          />
        )}

        {isSocialUsername && !showMarketingCheckbox && (
          <Text
            variant="body-small-regular"
            className="p-4 text-center text-secondary"
            data-testid="EmailForm__MarketingCopy"
          >
            {t(
              'By continuing, you agree to receive offers, news and updates from Linktree'
            )}
          </Text>
        )}

        <Button
          onClick={handleContinue}
          disabled={!isFormValid}
          label={t('Continue')}
          state={isLoading ? 'loading' : 'default'}
        />
      </div>
    </Layout>
  );
};

export default UsernameScreen;
